import { useMemo, useRef, useState, type CSSProperties } from "react";
import { Box, alpha, useTheme } from "@mui/material";
import type { Espelho, EspelhoColaborador, TipoDia } from "@/api/escala.service";
import {
  estabilizarGrade,
  montarGrade,
  type GradeEspelho,
  type InfracoesPorColaborador,
  type PendenciasPorColaborador,
} from "@/components/escala/espelho";
import { estilosDoTema, fundoDaHora } from "@/components/escala/tiposDia";
import { EspelhoCabecalho, TOTAIS_ESPELHO, TOTAL_HORAS, type ColunaTotal } from "@/components/escala/EspelhoCabecalho";
import { EspelhoLinha } from "@/components/escala/EspelhoLinha";
import { EspelhoRodape } from "@/components/escala/EspelhoRodape";
import { AlcaLarguraColuna } from "@/components/escala/AlcaLarguraColuna";
import {
  ALTURA_TOTAIS,
  LARGURA_COLABORADOR,
  LARGURA_COLABORADOR_MAX,
  LARGURA_COLABORADOR_MIN,
  VAR_LARGURA_COLABORADOR,
} from "@/components/escala/espelhoEstilos";

const TOTAL_DOMINGOS: ColunaTotal = { rotulo: "DT", titulo: "Domingos trabalhados" };

// Largura em que todo texto da coluna do nome cabe sem reticências: a de hoje, menos o espaço que
// cada texto tem, mais o que ele ocupa por inteiro. Nomes e detalhes das linhas, e o nome das
// cargas no rodapé.
function larguraQueCabeTudo(tabela: HTMLTableElement, atual: number) {
  let necessaria = LARGURA_COLABORADOR_MIN;
  for (const el of Array.from(tabela.querySelectorAll<HTMLElement>("tbody tr > td:first-child [title], tfoot tr > td:first-child"))) {
    const texto = document.createRange();
    texto.selectNodeContents(el);
    const estilo = getComputedStyle(el);
    const espaco = el.clientWidth - (parseFloat(estilo.paddingLeft) || 0) - (parseFloat(estilo.paddingRight) || 0);
    necessaria = Math.max(necessaria, atual - espaco + texto.getBoundingClientRect().width);
  }
  // Arredondado pra cima e com folga de 1px: a fração de pixel já faz a reticência voltar.
  return Math.min(LARGURA_COLABORADOR_MAX, Math.ceil(necessaria) + 1);
}

interface EspelhoGradeProps {
  espelho: Espelho;
  onCiclar?: (colaboradorId: string, iso: string, tipo: TipoDia) => void;
  // Só vale na escala editável com carga horária.
  onTrocarCarga?: (colaboradorId: string, iso: string) => void;
  onVerLog?: (colaborador: EspelhoColaborador) => void;
  // Só na escala com carga aprovada.
  onImprimirEscalinha?: (colaborador: EspelhoColaborador) => void;
  infracoes?: InfracoesPorColaborador;
  // Quem tem ocorrência ou aviso ganha o selo no avatar.
  pendencias?: PendenciasPorColaborador;
  // Esconde as colunas que não são domingo (mês e histórico) e acrescenta "Domingos trabalhados".
  soDomingos?: boolean;
  // Esconde o rodapé de totais (Trabalhando, Folgando, Compensação e cargas) e as colunas TT, TF e TH,
  // que não cabem em telas menores e nem sempre são o que se quer olhar.
  mostrarRodape?: boolean;
}

export function EspelhoGrade({
  espelho,
  onCiclar,
  onTrocarCarga,
  onVerLog,
  onImprimirEscalinha,
  infracoes,
  pendencias,
  soDomingos = false,
  mostrarRodape = true,
}: EspelhoGradeProps) {
  const theme = useTheme();
  const anterior = useRef<GradeEspelho | undefined>(undefined);
  const grade = useMemo(() => {
    const nova = estabilizarGrade(montarGrade(espelho), anterior.current);
    anterior.current = nova;
    return nova;
  }, [espelho]);
  const estilos = useMemo(() => estilosDoTema(theme), [theme]);
  const fundoHora = useMemo(() => fundoDaHora(theme), [theme]);

  // Coluna do nome redimensionável: a largura vai na variável CSS da tabela, e as linhas (memo)
  // não redesenham durante o arraste. Cada espelho abre na largura padrão.
  const tabela = useRef<HTMLTableElement>(null);
  const [larguraNome, setLarguraNome] = useState(LARGURA_COLABORADOR);
  const ajustarLarguraNome = () => {
    if (tabela.current) setLarguraNome(larguraQueCabeTudo(tabela.current, larguraNome));
  };

  // Filtro só de exibição: os totais por colaborador continuam sendo os do mês inteiro.
  const visivel = useMemo(() => {
    if (!soDomingos) {
      return {
        colunas: grade.colunas,
        porDia: grade.porDia,
        cargas: grade.cargas,
        historico: grade.historico.colunas,
        domingos: undefined,
      };
    }
    const indices = grade.colunas.flatMap((c, i) => (c.domingo ? [i] : []));
    const domingos = new Map<string, number>();
    for (const c of espelho.colaboradores) {
      const linha = grade.celulas.get(c.id);
      domingos.set(c.id, indices.filter((i) => linha?.get(grade.colunas[i]!.dia)?.tipo === "T").length);
    }
    return {
      colunas: indices.map((i) => grade.colunas[i]!),
      porDia: indices.map((i) => grade.porDia[i]!),
      cargas: grade.cargas.map((c) => ({ ...c, porDia: indices.map((i) => c.porDia[i]!) })),
      historico: grade.historico.colunas.filter((c) => c.domingo),
      domingos,
    };
  }, [soDomingos, grade, espelho.colaboradores]);
  // DT com o filtro de domingos; TT, TF e, na escala com carga, TH com os totais à mostra.
  const comHoras = !!espelho.gerada.com_carga_horaria;
  const totais = [
    ...(visivel.domingos ? [TOTAL_DOMINGOS] : []),
    ...(mostrarRodape ? [...TOTAIS_ESPELHO, ...(comHoras ? [TOTAL_HORAS] : [])] : []),
  ];

  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 3,
        bgcolor: "background.paper",
        overflow: "auto",
        maxHeight: "100%",
        boxShadow: `0 1px 3px ${alpha(theme.palette.common.black, 0.06)}`,
      }}
    >
      <Box
        component="table"
        ref={tabela}
        aria-label="Grade da escala"
        sx={{ borderCollapse: "separate", borderSpacing: 0, fontSize: 12, width: "max-content", minWidth: soDomingos ? 0 : "100%" }}
        style={{ [VAR_LARGURA_COLABORADOR]: `${larguraNome}px` } as CSSProperties}
      >
        <EspelhoCabecalho
          colunas={visivel.colunas}
          colunasHistorico={visivel.historico}
          totais={totais}
          alcaColaborador={
            <AlcaLarguraColuna
              largura={larguraNome}
              min={LARGURA_COLABORADOR_MIN}
              max={LARGURA_COLABORADOR_MAX}
              rotulo="Largura da coluna Colaborador"
              onMudar={setLarguraNome}
              onAjustar={ajustarLarguraNome}
            />
          }
        />
        <tbody>
          {espelho.colaboradores.map((colaborador) => (
            <EspelhoLinha
              key={colaborador.id}
              colaborador={colaborador}
              colunas={visivel.colunas}
              celulas={grade.celulas.get(colaborador.id)}
              colunasHistorico={visivel.historico}
              celulasHistorico={grade.historico.celulas.get(colaborador.id)}
              totais={grade.porColaborador.get(colaborador.id)!}
              domingosTrabalhados={visivel.domingos?.get(colaborador.id)}
              mostrarHoras={comHoras}
              mostrarTotais={mostrarRodape}
              estilos={estilos}
              fundoHora={fundoHora}
              onCiclar={espelho.editavel ? onCiclar : undefined}
              onTrocarCarga={espelho.editavel && espelho.gerada.com_carga_horaria ? onTrocarCarga : undefined}
              onVerLog={onVerLog}
              onImprimirEscalinha={onImprimirEscalinha}
              infracoes={infracoes?.get(colaborador.id)}
              pendencia={pendencias?.get(colaborador.id)}
            />
          ))}
        </tbody>
        {mostrarRodape ? (
          <EspelhoRodape
            colunas={visivel.colunas}
            colunasHistorico={visivel.historico.length}
            colunasTotais={totais.length}
            porDia={visivel.porDia}
            cargas={visivel.cargas}
            minimo={espelho.gerada.quantidade_minima}
            minimoDomingo={espelho.gerada.quantidade_minima_domingo}
          />
        ) : (
          // Sem o rodapé, nada mais garante o canto vazio que o botão flutuante de imprimir e a
          // legenda esperam encontrar nos meses sem feriado (ver comentário em
          // EscalaEspelhoView.tsx) — sem isso, eles caem sobre o fim da linha do último colaborador. Esse
          // espaçador fica colado na base, do tamanho de dois totais, sem nenhum conteúdo.
          <tfoot>
            <Box component="tr">
              <Box
                component="td"
                colSpan={1 + visivel.historico.length + visivel.colunas.length + totais.length}
                sx={{ position: "sticky", bottom: 0, zIndex: 3, height: ALTURA_TOTAIS * 2, bgcolor: "background.paper" }}
              />
            </Box>
          </tfoot>
        )}
      </Box>
    </Box>
  );
}
